import type { ManagerProfile, UserResponse } from "@sidegigllc/api/client";
import type { QueryClient } from "@tanstack/react-query";
import { createRoute, notFound, type ParsedLocation, redirect } from "@tanstack/react-router";
import { activeLocationsQueryOptions } from "api/queries/locations";
import { deriveOnboardingGates } from "domains/auth/providers/Root/helpers";
import { completeNativeHandoff } from "domains/auth/queries/nativeHandoff";
import { getStoredLocationId, parseUrlLocationId, swapLocationInPath } from "services/atoms";
import { SignedUpGuard } from "../SignedUpGuard";
import { authedRoute } from "./authed";
import { pickLocationId } from "./pickLocationId";

// Account linking no longer blocks here — AuthProvider surfaces it as a modal over any authed route.
const resolveSignUp = async (
  queryClient: QueryClient,
  user: UserResponse,
  managerProfile: ManagerProfile | undefined,
  location: ParsedLocation,
): Promise<{ hasLocations: boolean; locationRequired: boolean }> => {
  const locations = await queryClient.query({ ...activeLocationsQueryOptions(), staleTime: "static" }).catch(() => {});
  const { hasLocations, signUpRequired, locationRequired } = deriveOnboardingGates({
    loaded: true,
    appMode: user.app_mode,
    managerProfileId: managerProfile?.id,
    profileHasLocations: managerProfile?.has_locations,
    activeLocationCount: locations?.length,
    isGigproAdmin: !!user.is_gigpro_admin,
  });
  if (!signUpRequired) return { hasLocations, locationRequired };

  const urlParams = new URLSearchParams(location.searchStr);
  throw redirect({
    to: "/signup",
    search: {
      ref: urlParams.get("ref") ?? undefined,
      returnTo: urlParams.get("returnTo") ?? undefined,
      skipAccountLinking: urlParams.get("skipAccountLinking") === "true" || undefined,
    },
  });
};

const requireAccessibleLocation = async (queryClient: QueryClient, user: UserResponse, location: ParsedLocation) => {
  if (user.is_gigpro_admin) return;
  const urlLocationId = parseUrlLocationId(location.pathname);
  if (urlLocationId === undefined) return;

  // A failed locations fetch can't prove the URL location is inaccessible — let it through (the location
  // loader guards the specific id) rather than rejecting beforeLoad into the error boundary.
  const locations = await queryClient.query({ ...activeLocationsQueryOptions(), staleTime: "static" }).catch(() => {});
  if (!locations || locations.length === 0 || locations.some((l) => l.id === urlLocationId)) return;

  throw redirect({
    href: `${swapLocationInPath(location.pathname, urlLocationId, locations[0].id)}${location.searchStr}`,
  });
};

export const signedUpRoute = createRoute({
  getParentRoute: () => authedRoute,
  id: "signedUp",
  beforeLoad: async ({ context: { auth, queryClient }, location }) => {
    const { user, managerProfile } = auth;

    const { hasLocations, locationRequired } = await resolveSignUp(queryClient, user, managerProfile, location);
    if (locationRequired && !location.pathname.startsWith("/locations")) {
      throw redirect({ to: "/locations" });
    }

    // Hand the session to the native app only now: behind the gates above, so a brand-new user finishes the
    // sign-up wizard before the app takes over. Redirects (system browser) or postMessage (WebView); no-op on web.
    if (hasLocations) await completeNativeHandoff();

    await requireAccessibleLocation(queryClient, user, location);
  },
  component: SignedUpGuard,
});

// Legacy location-relative entry points (e.g. `/schedule`) carry no location prefix; send an
// authenticated manager to their active location's home rather than the not-found shell.
export const signedUpFallbackRoute = createRoute({
  getParentRoute: () => signedUpRoute,
  path: "$",
  beforeLoad: async ({ context: { auth, queryClient }, location }) => {
    // Already location-prefixed: an unknown child, not a legacy path — re-prefixing would loop.
    if (location.pathname.startsWith("/locations/")) throw notFound();
    const locations = await queryClient.query({ ...activeLocationsQueryOptions(), staleTime: "static" });
    if (locations.length === 0) throw redirect({ href: "/locations" });
    const locationId = pickLocationId(locations, getStoredLocationId(), auth.user.is_gigpro_admin);
    throw redirect({ href: `/locations/${locationId}${location.pathname}${location.searchStr}` });
  },
});
